import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  PersonAdd as ReferralIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { checkReferralCode, validateReferralCode, ReferralCodeValidation } from '../services/referralCode';
import { useAuth } from '../contexts/AuthContext';
import { trackReferralCodeApplied } from '../services/analytics';

interface ReferralCodeInputProps {
  onSuccess?: (validation: ReferralCodeValidation) => void;
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  showSuccessMessage?: boolean;
  placeholder?: string;
  label?: string;
}

const ReferralCodeInput: React.FC<ReferralCodeInputProps> = ({
  onSuccess,
  variant = 'outlined',
  size = 'medium',
  fullWidth = true,
  showSuccessMessage = true,
  placeholder = "Enter referral code",
  label = "Referral Code"
}) => {
  const { currentUser, getIdToken } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<ReferralCodeValidation | null>(null);
  const [showAlert, setShowAlert] = useState(false);

  const handleCheckCode = async () => {
    if (!code.trim()) return;
    
    setLoading(true);
    setValidation(null);
    
    try {
      console.log('🔐 ReferralCodeInput - Current user:', currentUser);
      console.log('🔐 ReferralCodeInput - User ID:', currentUser?.uid);
      console.log('🔐 ReferralCodeInput - User email:', currentUser?.email);
      
      // Check if user is authenticated before proceeding
      if (!currentUser) {
        setValidation({
          isValid: false,
          message: 'You must be signed in to use referral codes. Please sign in first.'
        });
        setShowAlert(true);
        setLoading(false);
        return;
      }
      
      const result = await checkReferralCode(code.trim());
      setValidation(result);
      setShowAlert(true);
      
      if (result.isValid && currentUser) {
        console.log('🔐 ReferralCodeInput - Auto-validating code for authenticated user');
        // Auto-validate if user is authenticated
        await handleValidateCode();
      }
    } catch (error) {
      console.error('Error checking referral code:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateCode = async () => {
    if (!currentUser || !code.trim()) return;
    
    setLoading(true);
    
    try {
      const result = await validateReferralCode(getIdToken, code.trim());
      console.log('🔍 ReferralCodeInput - Validation result:', result);
      console.log('🔍 ReferralCodeInput - Result type:', typeof result);
      console.log('🔍 ReferralCodeInput - Result keys:', Object.keys(result || {}));
      console.log('🔍 ReferralCodeInput - Result message:', result?.message);
      console.log('🔍 ReferralCodeInput - Result isValid:', result?.isValid);
      
      setValidation(result);
      setShowAlert(true);
      
      if (result.isValid && onSuccess) {
        onSuccess(result);
        setCode(''); // Clear input on success
        
        // Track referral code usage
        trackReferralCodeApplied(
          result.code || code,
          result.affiliateName
        );
      }
    } catch (error) {
      console.error('Error validating referral code:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleCheckCode();
    }
  };

  const getAffiliateText = (validation: ReferralCodeValidation) => {
    if (!validation.affiliateName) return '';
    return `via ${validation.affiliateName}`;
  };

  const getUsageText = (validation: ReferralCodeValidation) => {
    if (!validation.maxUses || !validation.currentUses) return '';
    return `${validation.currentUses}/${validation.maxUses} uses`;
  };

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          label={label}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyPress={handleKeyPress}
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          placeholder={placeholder}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <ReferralIcon color="primary" />
              </InputAdornment>
            ),
            endAdornment: code && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setCode('');
                    setValidation(null);
                    setShowAlert(false);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <Button
          variant="contained"
          onClick={handleCheckCode}
          disabled={loading || !code.trim()}
          size={size}
          sx={{ 
            minWidth: 'fit-content',
            px: 2
          }}
        >
          {loading ? 'Checking...' : 'Apply'}
        </Button>
      </Box>

      <Collapse in={showAlert && validation !== null}>
        <Alert
          severity={validation?.isValid || validation?.message?.includes('applied') || validation?.message?.includes('referred') ? 'success' : 'error'}
          icon={validation?.isValid || validation?.message?.includes('applied') || validation?.message?.includes('referred') ? <CheckIcon /> : <ErrorIcon />}
          sx={{ mt: 2 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setShowAlert(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {validation?.message}
            </Typography>
            
            {(validation?.isValid || validation?.message?.includes('applied') || validation?.message?.includes('referred')) && (
              <Box sx={{ mt: 1 }}>
                {validation.affiliateName && (
                  <Chip
                    label={getAffiliateText(validation)}
                    color="success"
                    size="small"
                    icon={<BusinessIcon />}
                    sx={{ mr: 1, mb: 1 }}
                  />
                )}
                
                {validation.maxUses && (
                  <Chip
                    label={getUsageText(validation)}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 1 }}
                  />
                )}
                
                {!currentUser && (
                  <Typography variant="caption" color="text.secondary">
                    Sign in to activate this referral code
                  </Typography>
                )}
                

              </Box>
            )}
          </Box>
        </Alert>
      </Collapse>
    </Box>
  );
};

export default ReferralCodeInput;
